<!-- @format -->
<template>
    <div class="dkim-integration-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Domain Authentication</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="currentStep === steps.loading" class="step loading-step">
                    <loading-screen-svg />
                </div>
                <div v-else-if="currentStep === steps.domain" class="step">
                    <p>
                        By updating your DNS with our DomainKeys Identified Mail (DKIM) records,
                        you'll increase deliverability with your email communication as well as help
                        prevent spoofing. This step will ensure receiving mail servers know that
                        email communication sent using GlassHive is legitimate and has been
                        authenticated.
                    </p>
                    <p>
                        To complete this, you'll need the ability to access and add CNAME records to
                        your DNS.
                    </p>
                    <p>Enter the root domain name you will use when sending out emails:</p>

                    <app-input
                        v-model="dkimIntegration.domain"
                        label="Domain"
                        placeholder="abctech.com"
                        description="If you plan on sending from 'marketing@abctech.com', then you would enter 'abctech.com'"
                    />

                    <div class="custom-selector">
                        <app-checkbox v-model="dkimIntegration.useCustomSelector" /> Use custom DKIM
                        selector
                    </div>

                    <app-input
                        v-if="dkimIntegration.useCustomSelector"
                        v-model="dkimIntegration.dkimSelector"
                        label=" "
                        placeholder="h"
                        description="Max 3 letters or numbers. This is useful to customize if you are attempting to use the same domain across multiple accounts."
                    />
                </div>
                <div
                    v-else-if="currentStep === steps.createRecords"
                    class="step create-records-step"
                >
                    <loading-screen-svg />
                    <p>Hold on while we create your DNS records...</p>
                </div>
                <div v-else-if="currentStep === steps.records" class="step">
                    <div v-if="dkimIntegration.isAuthenticated === true">
                        <h4><r3-checkmark-svg /> Success!</h4>
                        <p>
                            Records verified. You can now send emails using
                            {{ dkimIntegration.domain }}.
                        </p>
                    </div>
                    <div v-else-if="dkimIntegration.isAuthenticated === false && !firstTimeSetup">
                        <h4><s3-close-svg /> Verification Error</h4>
                        <div>
                            <p>
                                First, let's see if the record is appearing, click the following
                                link:
                            </p>
                            <p>
                                <a
                                    target="_blank"
                                    :href="`https://emailstuff.org/dns/txt/${dkimIntegration.records[1].host}`"
                                    >https://emailstuff.org/dns/txt/{{
                                        dkimIntegration.records[1].host
                                    }}</a
                                >
                            </p>
                        </div>
                        <p>
                            If this page tells you that "No CNAME record found for
                            {{ dkimIntegration.records[1].host }}", and you have added your record,
                            check your TTL value. This value - the "time to live" - is the number of
                            seconds DNS servers will cache your record for. Lowering this value will
                            make the record propagate faster.
                        </p>
                        <p>
                            If the record still doesn't propagate, you will need to talk to your DNS
                            or web host and ask them to help you out. From our side, we can only see
                            what is there, not make any changes.
                        </p>
                        <p>
                            If it looks like the record is there and correct, then contact support.
                            It will help if you mention the domain name you are trying to add
                            records for.
                        </p>
                    </div>
                    <div v-else>
                        <p>
                            Copy the following records to your DNS. For help, please refer to your
                            DNS's specific instructions on how to add a CNAME record. Make sure to
                            adjust the Time to Live (TTL) to the lowest possible setting so that we
                            can verify quickly.
                        </p>
                    </div>
                    <master-table
                        class="cname-table"
                        tableName="cname_domain_authentication"
                        :initialSort="initialSort"
                        initialSortDirection="asc"
                        :items="dkimIntegration.records"
                        :totalItems="dkimIntegration.records.length"
                        :searchable="false"
                        :isInitializing="loading"
                        :refreshEvents="['cname:update']"
                        @query="query($event)"
                    >
                        <master-table-row
                            slot="master-table-row"
                            slot-scope="{ item }"
                            :item="item"
                        >
                            <master-table-cell
                                slot="master-table-cell"
                                label="Type"
                                :classes="['type-cell']"
                            >
                                {{ item.type }}
                            </master-table-cell>
                            <master-table-cell
                                slot="master-table-cell"
                                label="Host"
                                :classes="['host-cell']"
                            >
                                <app-tooltip :value="item.host" />
                            </master-table-cell>
                            <master-table-cell
                                slot="master-table-cell"
                                label="Data"
                                :classes="['data-cell']"
                            >
                                <app-tooltip :value="item.data" />
                            </master-table-cell>
                        </master-table-row>
                    </master-table>

                    <div class="instructions">
                        <div class="instruction">
                            <label>GoDaddy</label>
                            <a
                                href="https://www.godaddy.com/help/add-a-cname-record-19236"
                                target="_blank"
                            >
                                https://www.godaddy.com/help/add-a-cname-record-19236</a
                            >
                        </div>
                        <div class="instruction">
                            <label>NameCheap</label>
                            <a
                                href="https://www.namecheap.com/support/knowledgebase/article.aspx/9646/2237/how-can-i-set-up-a-cname-record-for-my-domain"
                                target="_blank"
                            >
                                https://www.namecheap.com/support/knowledgebase/article.aspx/9646/2237/how-can-i-set-up-a-cname-record-for-my-domain
                            </a>
                        </div>
                        <div class="instruction">
                            <label>BlueHost</label>
                            <a href="https://my.bluehost.com/hosting/help/cname" target="_blank">
                                https://my.bluehost.com/hosting/help/cname
                            </a>
                        </div>
                        <div class="instruction">
                            <label>HostGator</label>
                            <a
                                href="https://support.hostgator.com/articles/how-to-change-dns-zones-mx-cname-and-a-records"
                                target="_blank"
                            >
                                https://support.hostgator.com/articles/how-to-change-dns-zones-mx-cname-and-a-records
                            </a>
                        </div>
                    </div>
                </div>
                <div v-if="currentStep === steps.validating" class="step validate-records-step">
                    <loading-screen-svg />
                    <p>Validating your DNS records...</p>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-button" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button
                        :loading="currentStep.loading"
                        @click="currentStep.action"
                        :disabled="!formattedDomain"
                    >
                        {{ currentStep.buttonText }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const MasterTable = () => import('@/components/master-table/master-table.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const S3CloseSvg = () => import('@/components/svg/grid/s3-close-svg');
const R3CheckmarkSvg = () => import('@/components/svg/grid/r3-checkmark-svg');
const AppToolTip = () => import('@/components/common/app-tooltip');
const AppCheckbox = () => import('@/components/common/app-checkbox');

import EventBus from '@/scripts/event-bus.js';
import log, { LogLevel } from '@/scripts/logger';

export default {
    name: 'dkim-integration-widget',

    components: {
        'master-table': MasterTable,
        'app-button': AppButton,
        'app-input': AppInput,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        's3-close-svg': S3CloseSvg,
        'r3-checkmark-svg': R3CheckmarkSvg,
        'app-tooltip': AppToolTip,
        'app-checkbox': AppCheckbox,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            dkimIntegration: {
                id: this.widget.dkimIntegrationId,
                clientId: this.widget.clientId,
                domain: null,
                dkimSelector: 'h',
                useCustomSelector: false,
                dkimId: null,
                isAuthenticated: null,
                records: [],
            },

            initialSort: {
                name: 'DkimDomain',
                value: 'DkimDomain',
            },

            loading: true,
            currentStep: null,
            firstTimeSetup: false,

            steps: {
                loading: {
                    title: 'Loading...',
                    buttonText: 'Loading...',
                    action: () => {},
                    loading: true,
                },

                domain: {
                    title: 'Choose domain',
                    buttonText: 'Next',
                    action: this.createRecords,
                    loading: false,
                },

                createRecords: {
                    title: 'Creating records...',
                    buttonText: 'Loading...',
                    action: () => {},
                    loading: true,
                },

                records: {
                    title: 'Apply records',
                    buttonText: 'Validate',
                    action: this.validate,
                    loading: false,
                },

                validating: {
                    title: 'Validating...',
                    buttonText: 'Validating...',
                    action: () => {},
                    loading: true,
                },
            },
        };
    },

    computed: {
        formattedDomain() {
            return this.dkimIntegration.domain ? this.dkimIntegration.domain.trim() : null;
        },
    },

    created() {
        if (this.dkimIntegration.id) {
            this.currentStep = this.steps.records;
            EventBus.$emit('cname:update');
        } else {
            this.currentStep = this.steps.domain;
        }
    },

    methods: {
        validate() {
            this.firstTimeSetup = false;
            this.currentStep = this.steps.validating;

            this.$api
                .post(`/api/SendGrid/DkimValidation?id=${this.dkimIntegration.dkimId}`)
                .then((response) => {
                    this.dkimIntegration.isAuthenticated = response.data;

                    var changes = [
                        {
                            name: 'DkimIsAuthenticated',
                            value: this.dkimIntegration.isAuthenticated,
                        },
                    ];

                    this.$api
                        .patch(`/api/DkimIntegrations/${this.dkimIntegration.id}`, changes)
                        .catch((error) => {
                            log.error(error);
                            throw error;
                        })
                        .finally(() => {
                            EventBus.$emit('dkim-integration:update');
                        });
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while validating your DNS records.',
                        { error },
                    );
                })
                .finally(() => {
                    this.currentStep = this.steps.records;
                });
        },

        getFilterParam() {
            return `$filter=Id eq ${this.dkimIntegration.id}`;
        },

        getPageParam(options) {
            return `$skip=${options.itemsPerPage * (options.currentPage - 1)}`;
        },

        getTopParam(options) {
            return `$top=${options.itemsPerPage}`;
        },

        getSelectParam() {
            return '$select=DkimId,DkimIsAuthenticated,DkimDomain,DkimMailCnameHost,DkimMailCnameValue,Dkim1Host,Dkim1Value,Dkim2Host,Dkim2Value';
        },

        getOrderParam(options) {
            if (options.sortProperty) {
                return `$orderby=${options.sortProperty.value} ${options.sortDirection}`;
            } else {
                return '';
            }
        },

        getQueryParams(options) {
            return [
                this.getFilterParam(options),
                this.getPageParam(options),
                this.getTopParam(options),
                this.getOrderParam(options),
                this.getSelectParam(),
                '$count=true',
            ].join('&');
        },

        mapItems(rawData) {
            return rawData.map((item) => {
                this.dkimIntegration.records = [
                    {
                        type: 'CNAME',
                        host: item.DkimMailCnameHost,
                        data: item.DkimMailCnameValue,
                    },
                    {
                        type: 'CNAME',
                        host: item.Dkim1Host,
                        data: item.Dkim1Value,
                    },
                    {
                        type: 'CNAME',
                        host: item.Dkim2Host,
                        data: item.Dkim2Value,
                    },
                ];

                this.currentStep = this.steps.records;
            });
        },

        query(options) {
            this.loading = true;

            var query = `/service/DkimIntegrations?${this.getQueryParams(options)}`;
            this.$api
                .get(query)
                .then((response) => {
                    this.dkimIntegration.dkimId = response.data.value[0].DkimId;
                    this.dkimIntegration.isAuthenticated =
                        response.data.value[0].DkimIsAuthenticated;
                    this.dkimIntegration.domain = response.data.value[0].DkimDomain;
                    this.items = this.mapItems(response.data.value);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'Failed to fetch your domain authentication.',
                        { error },
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        createRecords() {
            if (this.formattedDomain) {
                var dkimSelector = this.dkimIntegration.useCustomSelector
                    ? this.dkimIntegration.dkimSelector
                    : 'h';

                if (!dkimSelector || dkimSelector.length > 3) {
                    this.$helpers.showFailNotification(
                        'DKIM selector must be between 1-3 letters.',
                        {
                            level: LogLevel.INFO,
                        },
                    );
                    return;
                }

                this.currentStep = this.steps.createRecords;
                var url = `/api/Clients/${this.dkimIntegration.clientId}/SendGrid/DkimAuthentication?domain=${this.formattedDomain}&dkimSelector=${dkimSelector}`;

                this.$api
                    .post(url)
                    .then((response) => {
                        this.dkimIntegration.id = response.data.Id;
                        this.dkimIntegration.dkimId = response.data.DkimId;
                        this.dkimIntegration.records = [
                            {
                                type: 'CNAME',
                                host: response.data.DkimMailCnameHost,
                                data: response.data.DkimMailCnameValue,
                            },
                            {
                                type: 'CNAME',
                                host: response.data.Dkim1Host,
                                data: response.data.Dkim1Value,
                            },
                            {
                                type: 'CNAME',
                                host: response.data.Dkim2Host,
                                data: response.data.Dkim2Value,
                            },
                        ];

                        this.currentStep = this.steps.records;
                        this.firstTimeSetup = true;

                        EventBus.$emit('dkim-integration:update');
                    })
                    .catch((error) => {
                        this.currentStep = this.steps.domain;
                        this.$helpers.showFailNotification(
                            'Failed to create your DNS record. Please try again.',
                            { error },
                        );
                    });
            }
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.dkim-integration-widget .cname-table .master-table-cell {
    height: auto;
    min-height: 52px;
}

.dkim-integration-widget .cname-table .type-cell {
    min-width: 100px;
    max-width: 100px;
}

.dkim-integration-widget .cname-table .host-cell,
.dkim-integration-widget .cname-table .data-cell {
    min-width: 400px;
    max-width: 400px;
}

.dkim-integration-widget .cname-table .host-cell .master-table-cell-content,
.dkim-integration-widget .cname-table .data-cell .master-table-cell-content {
    white-space: normal;
    overflow: visible;
    word-break: break-all;
}

.dkim-integration-widget .create-records-step,
.dkim-integration-widget .validate-records-step,
.dkim-integration-widget .loading-step {
    text-align: center;
}

.dkim-integration-widget .instructions .instruction {
    margin-top: 25px;
}

.dkim-integration-widget .instructions .instruction label {
    display: block;
}

.dkim-integration-widget .custom-selector {
    display: flex;
}

.dkim-integration-widget .custom-selector .app-checkbox {
    margin-right: 10px;
}
</style>
